import { FC } from 'react';
import {
  CommentsPreview,
  PageContentLayout,
  PageHeaderLayout,
  Loader,
  NothingFoundAlert,
  ReactionsPreview,
  SideMenu,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { useTabsState } from 'utils/Tabs';
import { useGuidedModeParam } from 'utils/useGuidedModeParam';
import { useRouteParams } from 'utils/useIdParam';
import { tabsSchema } from 'utils/schemas';
import { useNew } from 'store/news';
import { useHistory } from 'react-router-dom';
import {
  NewActions,
  NewsDisabledFeatureAlert,
  NewsThreadModal,
} from 'containers/news';
import { ReactionsModal } from 'containers/reactions';
import { useModalContext } from 'store/modal';
import Recipients from './recipients/Recipients';
import { NewCommentsList, NewContent, NewSettings } from './atoms';

const newsDetailsTabsSchema = tabsSchema([
  'content',
  'recipients',
  'settings',
  'comments',
]);

export const NewDetails: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { openModal } = useModalContext();
  const { id: newId } = useRouteParams(ROUTES.news.details);
  const { value: guided } = useGuidedModeParam();

  const sideTabs = useTabsState(
    [
      {
        key: 'content',
        label: t('benefitTemplates.templateContent'),
      },
      {
        key: 'recipients',
        label: t('common.recipients'),
      },
      {
        key: 'settings',
        label: t('common.settings'),
      },
      {
        key: 'comments',
        label: t('comments.title'),
      },
    ],
    newsDetailsTabsSchema,
  );

  const { data: newItem, isLoading } = useNew(newId);

  if (isLoading) return <Loader />;
  if (!newItem) return <NothingFoundAlert />;

  return (
    <Stack gap={2}>
      <NewsDisabledFeatureAlert />

      <Stack>
        <PageHeaderLayout
          breadcrumbs={[
            {
              label: t('permissions.newsModule'),
              url: ROUTES.news.list,
            },
            {
              label: newItem.name,
            },
          ]}
          side={
            <NewActions
              id={newItem.id}
              name={newItem.name}
              status={newItem.status}
              reactions={newItem.reactions}
            />
          }
          under={
            <Stack flexDirection="row" gap={2}>
              <ReactionsPreview
                reactions={newItem.reactions.summary}
                onClick={() => {
                  openModal({
                    component: ReactionsModal,
                    props: { hostId: newId },
                  });
                }}
              />
              <CommentsPreview
                count={newItem.commentsCount}
                onClick={() => {
                  openModal({
                    component: NewsThreadModal,
                    props: {
                      newId,
                    },
                    size: 'xl',
                  });
                }}
              />
            </Stack>
          }
        />

        <PageContentLayout
          sidebar={
            <SideMenu
              activeTab={sideTabs.activeTab}
              onTabChange={sideTabs.goTo}
              tabs={sideTabs.tabs}
            />
          }
        >
          {sideTabs.activeTab === 'content' && (
            <NewContent
              onSaveSuccess={() => {
                if (guided) sideTabs.next();
              }}
            />
          )}
          {sideTabs.activeTab === 'recipients' && (
            <Recipients
              onSaveSuccess={() => {
                if (guided) sideTabs.next();
              }}
            />
          )}
          {sideTabs.activeTab === 'settings' && (
            <NewSettings
              onSaveSuccess={() => {
                if (guided) history.push(ROUTES.news.list);
              }}
            />
          )}
          {sideTabs.activeTab === 'comments' && <NewCommentsList />}
        </PageContentLayout>
      </Stack>
    </Stack>
  );
};
